import React, { useRef, useCallback, useState, useEffect } from 'react';

type Props = {
  minWidth?: number;
  maxWidth?: number;
  storageKey?: string;
};

const STORAGE_KEY = 'virtual_counter_aside_width';

export const HorizontalResizableContainer: React.FC<Props> = ({
  children,
  minWidth,
  maxWidth,
  storageKey = STORAGE_KEY
}) => {
  const resizeRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const getStoredWidth = useCallback(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const width = parseInt(stored, 10);
      if (!isNaN(width)) {
        let validWidth = width;
        if (minWidth) {
          validWidth = Math.max(minWidth, validWidth);
        }
        if (maxWidth) {
          validWidth = Math.min(maxWidth, validWidth);
        }
        return validWidth;
      }
    }
    return minWidth;
  }, [storageKey, minWidth, maxWidth]);

  const saveWidth = useCallback(
    (width: number) => {
      localStorage.setItem(storageKey, width.toString());
    },
    [storageKey]
  );

  useEffect(() => {
    const storedWidth = getStoredWidth();
    if (resizeRef.current && storedWidth) {
      resizeRef.current.style.width = `${storedWidth}px`;
    }
  }, [getStoredWidth]);

  const resize = useCallback(
    (e: MouseEvent) => {
      if (!resizeRef.current) {
        return;
      }
      const handleWidth = 2;

      let width = window.innerWidth - e.clientX - handleWidth;

      if (minWidth) {
        width = Math.max(minWidth, width);
      }
      if (maxWidth) {
        width = Math.min(maxWidth, width);
      }
      resizeRef.current.style.width = `${width}px`;
      saveWidth(width);
    },
    [minWidth, maxWidth, saveWidth]
  );

  const handleonDragStart = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!resizeRef.current) {
        return;
      }

      e.preventDefault();
      document.addEventListener('mousemove', resize);
      document.addEventListener('mouseup', () => {
        document.removeEventListener('mousemove', resize);
      });
    },
    [resize]
  );

  const initialWidth = getStoredWidth() || minWidth;

  return (
    <div ref={resizeRef} style={{ display: 'flex', width: `${initialWidth}px`, minWidth: `${minWidth}px` }}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        onMouseDown={handleonDragStart}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          width: isHovered ? '4px' : '2px',
          backgroundColor: '#E0E0E0',
          cursor: 'col-resize',
          transition: 'width 0.2s'
        }}
      />
      {children}
    </div>
  );
};
